/**
 * Audio player (§20).
 *
 * The player is the platform's own `<audio>` element — the app decodes nothing.
 * That is not laziness; it is the §20 rule that patented decoders are never
 * bundled, expressed as code. It also means the honest answer to "can this
 * play?" comes from the element itself: `canPlayType` is asked before the
 * controls are shown, and when the platform lacks the codec the viewer says so
 * plainly (§19) rather than presenting a player that will silently fail.
 *
 * Editing is trimming, and only trimming. Two handles select a range against
 * the real, playing timeline; committing queues a lossless stream-copy cut
 * (§13). Nothing is re-encoded, so what plays back is exactly the original
 * samples inside the chosen range.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import type { AudioModel } from '@moduviewer/handlers';
import { mediaHeadline } from '@moduviewer/handlers';
import { Icon } from '@moduviewer/design/icon';

export interface AudioViewerProps {
  readonly model: AudioModel;
  readonly bytes: Uint8Array;
  readonly editing: boolean;
  readonly onTrim: (startSec: number, endSec: number) => void;
}

function formatTime(sec: number): string {
  if (!Number.isFinite(sec) || sec < 0) return '0:00';
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
}

export function AudioViewer({
  model, bytes, editing, onTrim,
}: AudioViewerProps): React.JSX.Element {
  const audio = useRef<HTMLAudioElement>(null);
  const [playable, setPlayable] = useState<boolean | null>(null);
  const [current, setCurrent] = useState(0);
  const duration = model.durationSec || 0;

  const [range, setRange] = useState<[number, number]>([0, duration]);
  useEffect(() => { setRange([0, model.durationSec || 0]); }, [model.durationSec]);

  // A local object URL for the bytes. §29: never a network URL — the file is
  // played from memory, and the URL is revoked when the view changes.
  const url = useMemo(
    () => URL.createObjectURL(new Blob([bytes as BlobPart], { type: model.mimeType })),
    [bytes, model.mimeType],
  );
  useEffect(() => () => URL.revokeObjectURL(url), [url]);

  // Ask the platform whether it can actually play this, before offering to.
  useEffect(() => {
    const el = audio.current;
    if (!el) return;
    const verdict = el.canPlayType(model.mimeType);
    setPlayable(verdict !== '');
  }, [model.mimeType, url]);

  const headline = mediaHeadline(model);
  const trimmed = range[0] > 0 || range[1] < duration;

  return (
    <div className="audio">
      <div className="audio__card">
        <div className="audio__art" aria-hidden="true">
          <Icon name="file-media" size={44} />
        </div>

        <div className="audio__meta">
          <h1 className="audio__title truncate">{model.tags['title'] || '오디오'}</h1>
          {model.tags['artist'] ? (
            <p className="audio__artist truncate">{model.tags['artist']}</p>
          ) : null}
          <dl className="audio__specs tnum">
            <div><dt>길이</dt><dd>{formatTime(duration)}</dd></div>
            {model.sampleRate ? <div><dt>표본율</dt><dd>{(model.sampleRate / 1000).toFixed(1)} kHz</dd></div> : null}
            {model.channels ? <div><dt>채널</dt><dd>{model.channels === 1 ? '모노' : model.channels === 2 ? '스테레오' : `${model.channels}ch`}</dd></div> : null}
            {model.bitrateKbps ? <div><dt>비트율</dt><dd>{model.bitrateKbps} kbps</dd></div> : null}
          </dl>
        </div>
      </div>

      <audio
        ref={audio}
        className="audio__player"
        src={url}
        controls
        preload="metadata"
        onTimeUpdate={(e) => setCurrent(e.currentTarget.currentTime)}
        aria-label={headline || '오디오 재생'}
      />

      {playable === false ? (
        <p className="audio__unsupported" role="status">
          <Icon name="alert" size={15} />
          이 기기의 코덱으로는 이 파일을 재생할 수 없습니다. 다른 앱에서 열어 주세요 (§20).
        </p>
      ) : null}

      {editing && model.trimmable ? (
        <div className="audio__trim">
          <div className="audio__trim-head">
            <span className="audio__trim-title">자르기</span>
            <span className="audio__trim-range tnum">
              {formatTime(range[0])} – {formatTime(range[1])}
              <span className="audio__trim-dur">({formatTime(range[1] - range[0])})</span>
            </span>
          </div>
          <div className="audio__trim-track">
            {/* Two overlaid range sliders, snapping the audio to the handle the
                user last moved so the cut point can be heard before it lands. */}
            <label className="visually-hidden" htmlFor="trim-start">시작 지점</label>
            <input
              id="trim-start" type="range" min={0} max={duration} step={0.01} value={range[0]}
              onChange={(e) => {
                const v = Math.min(Number(e.target.value), range[1] - 0.05);
                setRange([v, range[1]]);
                if (audio.current) audio.current.currentTime = v;
              }}
            />
            <label className="visually-hidden" htmlFor="trim-end">끝 지점</label>
            <input
              id="trim-end" type="range" min={0} max={duration} step={0.01} value={range[1]}
              onChange={(e) => {
                const v = Math.max(Number(e.target.value), range[0] + 0.05);
                setRange([range[0], v]);
                if (audio.current) audio.current.currentTime = v;
              }}
            />
            <div
              className="audio__trim-playhead"
              style={{ left: duration > 0 ? `${(current / duration) * 100}%` : '0%' }}
              aria-hidden="true"
            />
          </div>
          <div className="audio__trim-actions">
            <button
              className="btn btn--primary"
              disabled={!trimmed}
              onClick={() => onTrim(range[0], range[1])}
            >
              이 범위로 자르기
            </button>
            <button
              className="btn btn--ghost"
              disabled={!trimmed}
              onClick={() => setRange([0, duration])}
            >
              전체 선택
            </button>
          </div>
          <p className="audio__trim-note">
            {/* §20 — say what the cut does and does not do. */}
            선택한 범위 밖을 잘라내며, 다시 인코딩하지 않는 무손실 방식입니다. 프레임 경계에 맞춰 미세하게 반올림됩니다.
          </p>
        </div>
      ) : null}
    </div>
  );
}
